import { getCurrentTenantId } from '@/lib/auth-utils';
import { supabase } from './supabase';

export enum PromptScope {
  Global = 0,
  Project = 1,
  Story = 2,
  Chapter = 3
}

export enum PromptType {
  SystemInstruction = 0,
  WritingAction = 1
}

export interface AiPrompt {
  id: number;
  user_id: string;
  tenant_id: string;
  name: string;
  content: string;
  scope: PromptScope;
  type: PromptType;
  project_id?: string | null;
  story_id?: number | null;
  chapter_id?: number | null;
  action_label?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAiPromptParams {
  name: string;
  content: string;
  scope: PromptScope;
  type?: PromptType;
  project_id?: string | null;
  story_id?: number | null;
  chapter_id?: number | null;
  action_label?: string | null;
  is_active?: boolean;
}

export interface UpdateAiPromptParams {
  id: number;
  name?: string;
  content?: string;
  action_label?: string | null;
  is_active?: boolean;
}

/**
 * Create the ai_prompts table with proper schema and RLS policies
 */
export async function createAiPromptsTable(): Promise<boolean> {
  try {
    console.log('Creating ai_prompts table...');

    const { error: tableError } = await supabase.rpc('exec_sql', {
      sql: `
        -- Create types if they don't exist
        DO $$ 
        BEGIN
            IF NOT EXISTS (SELECT 1 FROM pg_type WHERE typname = 'prompt_scope') THEN
                CREATE TYPE prompt_scope AS ENUM ('global', 'project', 'story', 'chapter');
            END IF;
            IF NOT EXISTS (SELECT 1 FROM pg_type WHERE typname = 'prompt_type') THEN
                CREATE TYPE prompt_type AS ENUM ('system_instruction', 'writing_action');
            END IF;
        END $$;

        -- Create ai_prompts table
        CREATE TABLE IF NOT EXISTS ai_prompts (
          id BIGINT GENERATED ALWAYS AS IDENTITY PRIMARY KEY,
          user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
          tenant_id UUID NOT NULL,
          
          name TEXT NOT NULL,
          content TEXT NOT NULL,
          
          scope SMALLINT NOT NULL CHECK (scope BETWEEN 0 AND 3),
          type SMALLINT NOT NULL DEFAULT 0 CHECK (type BETWEEN 0 AND 1),
          
          -- Scoping fields
          project_id INT8 REFERENCES projects(id) ON DELETE CASCADE,
          story_id INT8 REFERENCES stories(id) ON DELETE CASCADE,
          chapter_id INT8 REFERENCES chapters(id) ON DELETE CASCADE,
          
          -- For writing actions
          action_label TEXT,
          
          is_active BOOLEAN DEFAULT TRUE,
          created_at TIMESTAMPTZ DEFAULT NOW(),
          updated_at TIMESTAMPTZ DEFAULT NOW(),

          -- Constraints
          CONSTRAINT valid_prompt_scope CHECK (
            (scope = 0 AND project_id IS NULL AND story_id IS NULL AND chapter_id IS NULL) OR
            (scope = 1 AND project_id IS NOT NULL AND story_id IS NULL AND chapter_id IS NULL) OR
            (scope = 2 AND story_id IS NOT NULL AND chapter_id IS NULL AND project_id IS NULL) OR
            (scope = 3 AND chapter_id IS NOT NULL AND story_id IS NULL AND project_id IS NULL)
          )
        );

        -- Create indexes
        CREATE INDEX IF NOT EXISTS idx_ai_prompts_user_id ON ai_prompts(user_id);
        CREATE INDEX IF NOT EXISTS idx_ai_prompts_scope ON ai_prompts(scope);
        CREATE INDEX IF NOT EXISTS idx_ai_prompts_context ON ai_prompts(project_id, story_id, chapter_id);
        CREATE INDEX IF NOT EXISTS idx_ai_prompts_type ON ai_prompts(type);
        CREATE INDEX IF NOT EXISTS idx_ai_prompts_tenant_id ON ai_prompts(tenant_id);
      `
    });

    if (tableError) {
      console.error('Error creating ai_prompts table:', tableError);
      throw tableError;
    }

    // Create RLS policies
    const { error: rlsError } = await supabase.rpc('exec_sql', {
      sql: `
        -- Enable RLS
        ALTER TABLE ai_prompts ENABLE ROW LEVEL SECURITY;

        -- Drop existing policies
        DROP POLICY IF EXISTS "Users can view their own ai prompts" ON ai_prompts;
        DROP POLICY IF EXISTS "Users can insert their own ai prompts" ON ai_prompts;
        DROP POLICY IF EXISTS "Users can update their own ai prompts" ON ai_prompts;
        DROP POLICY IF EXISTS "Users can delete their own ai prompts" ON ai_prompts;

        -- Create RLS policies with Tenant Check
        CREATE POLICY "Users can view their own ai prompts"
          ON ai_prompts FOR SELECT
          USING (
            auth.uid() = user_id AND 
            tenant_id IN (
              SELECT tenant_id FROM user_tenants WHERE user_id = auth.uid()
            )
          );

        CREATE POLICY "Users can insert their own ai prompts"
          ON ai_prompts FOR INSERT
          WITH CHECK (
            auth.uid() = user_id AND 
            tenant_id IN (
              SELECT tenant_id FROM user_tenants WHERE user_id = auth.uid()
            )
          );

        CREATE POLICY "Users can update their own ai prompts"
          ON ai_prompts FOR UPDATE
          USING (
            auth.uid() = user_id AND 
            tenant_id IN (
              SELECT tenant_id FROM user_tenants WHERE user_id = auth.uid()
            )
          );

        CREATE POLICY "Users can delete their own ai prompts"
          ON ai_prompts FOR DELETE
          USING (
            auth.uid() = user_id AND 
            tenant_id IN (
              SELECT tenant_id FROM user_tenants WHERE user_id = auth.uid()
            )
          );
      `
    });

    if (rlsError) {
      console.error('Error creating RLS policies for ai_prompts:', rlsError);
      throw rlsError;
    }

    console.log('ai_prompts table and policies created successfully');
    return true;
  } catch (error) {
    console.error('Error in createAiPromptsTable:', error);
    throw error;
  }
}

/**
 * Get prompts filtered by scope and context
 */
export async function getAiPrompts(
  scope?: PromptScope,
  contextId?: string | number,
  type?: PromptType
): Promise<AiPrompt[]> {
  try {
    let query = supabase
      .from('ai_prompts')
      .select('*')
      .order('created_at', { ascending: false });

    if (scope !== undefined) {
      query = query.eq('scope', scope);
      
      if (contextId) {
        if (scope === PromptScope.Project) query = query.eq('project_id', contextId);
        if (scope === PromptScope.Story) query = query.eq('story_id', contextId);
        if (scope === PromptScope.Chapter) query = query.eq('chapter_id', contextId);
      }
    }

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching ai prompts:', error);
    throw error;
  }
}

/**
 * Get prompts specifically for the current context (Project + Story)
 * Excludes Global prompts as per user request
 */
export async function getPromptsForContext(
  projectId: string, 
  storyId?: string | number | null
): Promise<AiPrompt[]> {
  try {
    let query = supabase
      .from('ai_prompts')
      .select('*')
      .order('created_at', { ascending: false });

    const conditions = [`and(scope.eq.${PromptScope.Project},project_id.eq.${projectId})`];
    
    if (storyId) {
      conditions.push(`and(scope.eq.${PromptScope.Story},story_id.eq.${storyId})`);
    }

    query = query.or(conditions.join(','));

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching context prompts:', error);
    throw error;
  }
}

/**
 * Create a new AI prompt
 */
export async function createAiPrompt(params: CreateAiPromptParams): Promise<AiPrompt> {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error('Not authenticated');

    const tenantId = await getCurrentTenantId();
    if (!tenantId) throw new Error('No tenant found');

    const { data, error } = await supabase
      .from('ai_prompts')
      .insert({
        ...params,
        user_id: user.id,
        tenant_id: tenantId,
        type: params.type !== undefined ? params.type : PromptType.SystemInstruction
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating ai prompt:', error);
    throw error;
  }
}

/**
 * Update an existing AI prompt
 */
export async function updateAiPrompt(params: UpdateAiPromptParams): Promise<AiPrompt> {
  try {
    const { id, ...updateData } = params;
    const { data, error } = await supabase
      .from('ai_prompts')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating ai prompt:', error);
    throw error;
  }
}

/**
 * Delete an AI prompt
 */
export async function deleteAiPrompt(id: number): Promise<void> {
  try {
    const { error } = await supabase
      .from('ai_prompts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting ai prompt:', error);
    throw error;
  }
}

/**
 * Fetch available writing actions based on current context
 * Returns all active writing actions matching the provided context hierarchy
 */
export async function fetchWritingActions(
  projectId?: string,
  storyId?: number,
  chapterId?: number
): Promise<AiPrompt[]> {
  try {
    let query = supabase
      .from('ai_prompts')
      .select('*')
      .eq('type', PromptType.WritingAction)
      .eq('is_active', true);

    const orConditions = [`scope.eq.${PromptScope.Global}`];
    
    if (projectId) {
      orConditions.push(`and(scope.eq.${PromptScope.Project},project_id.eq.${projectId})`);
    }
    
    if (storyId) {
      orConditions.push(`and(scope.eq.${PromptScope.Story},story_id.eq.${storyId})`);
    }
    
    if (chapterId) {
      orConditions.push(`and(scope.eq.${PromptScope.Chapter},chapter_id.eq.${chapterId})`);
    }

    query = query.or(orConditions.join(','));

    const { data, error } = await query;

    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching writing actions:', error);
    return [];
  }
}
